import tinycolor from 'tinycolor2';
const brandColors = {
    butter: {
        dark: '#bb6802',
        light: '#fff2cc',
        pastel: '#ffd966',
        primary: '#ffbf00',
        shadow: '#7c420b',
    },
    cobalt: {
        dark: '#142fe1',
        light: '#d6e0ff',
        pastel: '#99b2ff',
        primary: '#3366ff',
        shadow: '#19288f',
    },
    jade: {
        dark: '#008058',
        light: '#cdf3e6',
        pastel: '#81e2bf',
        primary: '#04c580',
        shadow: '#01533b',
    },
    violet: {
        dark: '#5c30d1',
        light: '#e8e4fc',
        pastel: '#c6baf8',
        primary: '#8d76f1',
        shadow: '#402290',
    },
    rose: {
        dark: '#c11574',
        light: '#fce7f3',
        pastel: '#faa7e0',
        primary: '#ee46bc',
        shadow: '#851651',
    },
};
const productColors = {
    blue: {
        25: '#f5f8ff',
        50: '#eff4ff',
        100: '#d1e0ff',
        200: '#b2ccff',
        300: '#84adff',
        400: '#528bff',
        500: '#3366FF',
        600: '#155eef',
        700: '#004eeb',
        800: '#0040c1',
        900: '#00359e',
    },
    error: {
        25: '#FFFBFA',
        50: '#FEF3F2',
        100: '#FEE4E2',
        200: '#ffcdca',
        300: '#fda29b',
        400: '#fa7066',
        500: '#F04438',
        600: '#d92d20',
        700: '#B42318',
        800: '#912018',
        900: '#7A271A',
    },
    gray: {
        25: '#FCFCFC',
        50: '#FAFAFA',
        100: '#F4F4F5',
        200: '#E4E4E7',
        300: '#D1D1D6',
        400: '#A0A0AB',
        500: '#70707B',
        600: '#51525C',
        700: '#3F3F46',
        800: '#26272b',
        900: '#18181B',
    },
    green: {
        25: '#F6FEF9',
        50: '#ECFDF3',
        100: '#D1FADF',
        200: '#A6F4C5',
        300: '#6CE9A6',
        400: '#32D583',
        500: '#12B76A',
        600: '#039855',
        700: '#027A48',
        800: '#05603A',
        900: '#054F31',
    },
    neutral: {
        25: '#f5f8ff',
        50: '#eff4ff',
        100: '#d1e0ff',
        200: '#b2ccff',
        300: '#84adff',
        400: '#528bff',
        500: '#3366FF',
        600: '#155eef',
        700: '#004eeb',
        800: '#0040c1',
        900: '#00359e',
    },
    orange: {
        100: '#FEF0C7',
        200: '#FEDF89',
        25: '#FFFCF5',
        300: '#FEC84B',
        400: '#FDB022',
        50: '#FFFAEB',
        500: '#F79009',
        600: '#DC6803',
        700: '#B54708',
        800: '#93370D',
        900: '#7A2E0E',
    },
    purple: {
        100: '#ECEAFD',
        200: '#DAD8FC',
        25: '#F8F8FF',
        300: '#BEB8FA',
        400: '#9D90F5',
        50: '#F4F4FE',
        500: '#8D76F1',
        600: '#6B42E5',
        700: '#5C30D1',
        800: '#4C28AF',
        900: '#402290',
        950: '#261461',
    },
    red: {
        100: '#FEE4E2',
        200: '#ffcdca',
        25: '#FFFBFA',
        300: '#fda29b',
        400: '#fa7066',
        50: '#FEF3F2',
        500: '#F04438',
        600: '#d92d20',
        700: '#B42318',
        800: '#912018',
        900: '#7A271A',
    },
    success: {
        100: '#D1FADF',
        200: '#A6F4C5',
        25: '#F6FEF9',
        300: '#6CE9A6',
        400: '#32D583',
        50: '#ECFDF3',
        500: '#12B76A',
        600: '#039855',
        700: '#027A48',
        800: '#05603A',
        900: '#054F31',
    },
    warning: {
        100: '#FEF0C7',
        200: '#FEDF89',
        25: '#FFFCF5',
        300: '#FEC84B',
        50: '#FFFAEB',
        400: '#FDB022',
        500: '#F79009',
        600: '#DC6803',
        700: '#B54708',
        800: '#93370D',
        900: '#7A2E0E',
    },
};
const reportChartColors = {
    color1: '#2563EB',
    color2: '#EA580C',
    color3: '#0D9488',
    color4: '#C026D3',
    color5: '#16A34A',
    color6: '#7C3AED',
    color7: '#CA8A04',
    color8: '#0284C7',
    color9: '#DC2626',
    color10: '#475569',
};
const reportThemeColors = {
    text: '#020617',
    textMuted: '#737373',
    background: '#FFFFFF',
    card: '#FFFFFF',
    primary: reportChartColors.color1,
    accent: reportChartColors.color2,
    comparisonPositive: '#16A34A',
    comparisonNegative: '#DC2626',
    chart: reportChartColors,
};
const guaranteeBrandingColors = {
    butter: {
        base: '#FFF2CC',
        highlight: '#FFF8E3',
    },
};
const defaultColors = {
    black: '#000000',
    white: '#ffffff',
};
const defaultFonts = {
    matter: 'Matter',
};
const getMostReadableHexColor = (backgroundColorHex, candidates = [defaultColors.white, productColors.gray[900]]) => {
    return tinycolor.mostReadable(backgroundColorHex, candidates).toHexString();
};
const generateColorPalette = (color, steps = 9) => {
    if (!tinycolor(color).isValid())
        return [color];
    const palette = [];
    for (let i = 1; i <= steps; i += 1) {
        const ratio = (90 / steps) * i;
        palette.push(tinycolor.mix(color, '#000000', ratio).toHexString(), tinycolor.mix(color, '#ffffff', ratio).toHexString());
    }
    return palette;
};
const ACCEPTABLE_CONTRAST_RATIO = 1.1;
const isAcceptableThemeColor = (themeColor) => tinycolor.readability('#FFFFFF', themeColor) >= ACCEPTABLE_CONTRAST_RATIO;
// ripped from react-color
const isValidHex = (hex) => {
    if (hex === 'transparent') {
        return true;
    }
    // disable hex4 and hex8
    const lh = String(hex).startsWith('#') ? 1 : 0;
    return hex.length !== 4 + lh && hex.length < 7 + lh && tinycolor(hex).isValid();
};
const isLight = (color) => tinycolor(color).getLuminance() > 0.9;
const isDarkColor = (color) => tinycolor(color).isDark();
const DEFAULT_BRAND_THEME_COLOR = '#3366ff';
const THEME_CONTENT_BACKGROUND_COLOR = '#ffffff';
const THEME_BODY_BACKGROUND_COLOR = '#f4f4f5';
const THEME_TEXT_COLOR = '#101828';
const deriveDesignFromThemeColor = (themeColor) => {
    const primary = themeColor && tinycolor(themeColor).isValid()
        ? tinycolor(themeColor).toHexString()
        : DEFAULT_BRAND_THEME_COLOR;
    return {
        primary,
        accent: tinycolor(primary).spin(150).toHexString(),
        text: THEME_TEXT_COLOR,
        backgroundContent: THEME_CONTENT_BACKGROUND_COLOR,
        backgroundEmail: THEME_BODY_BACKGROUND_COLOR,
    };
};
export { brandColors, defaultColors, defaultFonts, productColors, reportThemeColors, guaranteeBrandingColors, isValidHex, getMostReadableHexColor, generateColorPalette, isAcceptableThemeColor, isDarkColor, isLight, deriveDesignFromThemeColor, DEFAULT_BRAND_THEME_COLOR, };
export * from './brand-palette';
